import { useEffect, useState, type FormEvent } from 'react';
import { api, type TheaterSettings, type User } from '../lib/api';
import './AdminPanel.css';

type AdminSection = 'users' | 'theater' | 'chat' | 'effects' | 'system';

export default function AdminPanel({
  open,
  onClose,
  theaterOccupants = [],
}: {
  open: boolean;
  onClose: () => void;
  theaterOccupants?: Array<{ userId: string; username: string; displayName: string | null }>;
}) {
  const [section, setSection] = useState<AdminSection>('users');
  const [users, setUsers] = useState<
    Array<User & { is_disabled: boolean; last_login_at: string | null }>
  >([]);
  const [viewers, setViewers] = useState<
    Array<{ id: string; username: string; display_name: string | null; role: string }>
  >([]);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'USER' | 'ADMIN'>('ADMIN');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<TheaterSettings | null>(null);
  const [chatStats, setChatStats] = useState<{ stored: number; limit: number } | null>(null);
  const [moderation, setModeration] = useState<{
    mutedUsers: string[];
    throwDisabledUsers: string[];
    removedUsers: string[];
  } | null>(null);
  const [chatQ, setChatQ] = useState('');
  const [chatMessages, setChatMessages] = useState<
    Array<{
      id: string;
      username_snapshot: string;
      message: string;
      created_at: string;
      movie_title: string | null;
    }>
  >([]);
  const [chatTotal, setChatTotal] = useState(0);
  const [chatOffset, setChatOffset] = useState(0);

  async function refresh() {
    const [u, v, t] = await Promise.all([
      api.users(),
      api.viewers(),
      api.adminTheaterSettings(),
    ]);
    setUsers(u.users);
    setViewers(v.viewers);
    setSettings(t.settings);
    setChatStats(t.chat);
    setModeration({
      mutedUsers: t.moderation.mutedUsers || [],
      throwDisabledUsers: t.moderation.throwDisabledUsers || [],
      removedUsers: t.moderation.removedUsers || [],
    });
  }

  async function loadChat(offset = 0) {
    const res = await api.adminChatHistory({
      q: chatQ || undefined,
      limit: 40,
      offset,
    });
    setChatMessages(res.messages);
    setChatTotal(res.total);
    setChatOffset(offset);
  }

  useEffect(() => {
    if (!open) return;
    void refresh().catch((err) => setError((err as Error).message));
  }, [open]);

  useEffect(() => {
    if (!open || section !== 'chat') return;
    void loadChat(0).catch((err) => setError((err as Error).message));
  }, [open, section]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  async function createUser(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await api.createUser({ username, password, role });
      setUsername('');
      setPassword('');
      setMessage('User created.');
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function patchSettings(patch: Partial<TheaterSettings>) {
    const res = await api.updateAdminTheaterSettings(patch);
    setSettings(res.settings);
    setMessage('Theater settings updated.');
  }

  const nav: Array<{ id: AdminSection; label: string }> = [
    { id: 'users', label: 'Users' },
    { id: 'theater', label: 'Theater' },
    { id: 'chat', label: 'Chat History' },
    { id: 'effects', label: 'Theater Effects' },
    { id: 'system', label: 'System' },
  ];

  return (
    <div className="admin-overlay" onClick={onClose}>
      <div className="admin-panel panel admin-panel-wide" onClick={(e) => e.stopPropagation()}>
        <div className="admin-head">
          <h2 className="section-title">ADMIN</h2>
          <button className="btn" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="admin-nav">
          {nav.map((n) => (
            <button
              key={n.id}
              type="button"
              className={`btn${section === n.id ? ' btn-gold' : ''}`}
              onClick={() => setSection(n.id)}
            >
              {n.label}
            </button>
          ))}
        </div>

        {message && <p className="muted">{message}</p>}
        {error && <div className="error-banner">{error}</div>}

        {section === 'users' && (
          <>
            <h3>Create user</h3>
            <form className="admin-form" onSubmit={createUser}>
              <input
                className="field"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
              <input
                className="field"
                type="password"
                placeholder="Password (12+ chars)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={12}
              />
              <select
                className="field"
                value={role}
                onChange={(e) => setRole(e.target.value as 'USER' | 'ADMIN')}
              >
                <option value="ADMIN">ADMIN</option>
                <option value="USER">USER</option>
              </select>
              <button className="btn btn-primary" type="submit">
                Create account
              </button>
            </form>

            <h3>Users</h3>
            <ul className="admin-list">
              {users.map((u) => (
                <li key={u.id} className="user-row">
                  <div>
                    <strong>{u.username}</strong>{' '}
                    <span className="muted">
                      {u.role}
                      {u.is_disabled ? ' · disabled' : ''}
                    </span>
                  </div>
                  <div className="user-actions">
                    <button
                      className="btn"
                      onClick={async () => {
                        const pw = window.prompt('New password (12+ characters)');
                        if (!pw) return;
                        try {
                          await api.resetPassword(u.id, pw);
                          setMessage(`Password reset for ${u.username}`);
                        } catch (err) {
                          setError((err as Error).message);
                        }
                      }}
                    >
                      Reset password
                    </button>
                    <button
                      className="btn"
                      onClick={async () => {
                        await api.disableUser(u.id, !u.is_disabled);
                        await refresh();
                      }}
                    >
                      {u.is_disabled ? 'Enable' : 'Disable'}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}

        {section === 'theater' && (
          <>
            <h3>Connected viewers</h3>
            <ul className="admin-list">
              {viewers.map((v) => (
                <li key={v.id}>
                  {v.display_name || v.username} <span className="muted">({v.role})</span>
                </li>
              ))}
              {viewers.length === 0 && <li className="muted">No viewers connected</li>}
            </ul>

            <h3>In theater</h3>
            <ul className="admin-list">
              {theaterOccupants.map((o) => (
                <li key={o.userId} className="user-row">
                  <div>{o.displayName || o.username}</div>
                  <div className="user-actions">
                    <button
                      className="btn"
                      onClick={async () => {
                        const muted = moderation?.mutedUsers.includes(o.userId);
                        await api.muteTheaterUser(o.userId, !muted);
                        await refresh();
                      }}
                    >
                      {moderation?.mutedUsers.includes(o.userId) ? 'Unmute' : 'Mute'}
                    </button>
                    <button
                      className="btn"
                      onClick={async () => {
                        const disabled = moderation?.throwDisabledUsers.includes(o.userId);
                        await api.disableTheaterThrows(o.userId, !disabled);
                        await refresh();
                      }}
                    >
                      {moderation?.throwDisabledUsers.includes(o.userId)
                        ? 'Enable throws'
                        : 'Disable throws'}
                    </button>
                    <button
                      className="btn"
                      onClick={async () => {
                        await api.removeFromTheater(o.userId);
                        await refresh();
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </li>
              ))}
              {theaterOccupants.length === 0 && <li className="muted">Nobody seated</li>}
            </ul>
          </>
        )}

        {section === 'chat' && (
          <>
            <p className="muted">
              Messages stored: {chatStats?.stored ?? 0} / {chatStats?.limit ?? 1000}
            </p>
            <div className="admin-chat-tools">
              <input
                className="field"
                placeholder="Search username, message, video..."
                value={chatQ}
                onChange={(e) => setChatQ(e.target.value)}
              />
              <button className="btn btn-gold" type="button" onClick={() => void loadChat(0)}>
                Search chat
              </button>
              <button
                className="btn"
                type="button"
                onClick={async () => {
                  if (
                    !window.confirm(
                      'This will permanently delete all retained theater chat messages.',
                    )
                  ) {
                    return;
                  }
                  await api.clearAdminChatHistory();
                  setMessage('Chat history cleared.');
                  await loadChat(0);
                  await refresh();
                }}
              >
                Clear history
              </button>
            </div>
            <ul className="admin-chat-list">
              {chatMessages.map((m) => (
                <li key={m.id}>
                  <div className="admin-chat-meta">
                    <span>
                      {new Date(m.created_at).toLocaleString()} · {m.username_snapshot}
                    </span>
                    <button
                      className="btn"
                      type="button"
                      onClick={async () => {
                        await api.deleteAdminChatMessage(m.id);
                        await loadChat(chatOffset);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                  <div>&ldquo;{m.message}&rdquo;</div>
                  <div className="muted">{m.movie_title || 'None'}</div>
                </li>
              ))}
            </ul>
            <div className="admin-actions">
              <button
                className="btn"
                disabled={chatOffset <= 0}
                onClick={() => void loadChat(Math.max(0, chatOffset - 40))}
              >
                Prev
              </button>
              <span className="muted">
                {chatOffset + 1} to {Math.min(chatOffset + chatMessages.length, chatTotal)} of{' '}
                {chatTotal}
              </span>
              <button
                className="btn"
                disabled={chatOffset + chatMessages.length >= chatTotal}
                onClick={() => void loadChat(chatOffset + 40)}
              >
                Next
              </button>
            </div>
          </>
        )}

        {section === 'effects' && settings && (
          <div className="effects-grid">
            <label className="pref-row">
              <span>Theater Effects</span>
              <select
                className="field"
                value={settings.effectsEnabled ? 'on' : 'off'}
                onChange={(e) => void patchSettings({ effectsEnabled: e.target.value === 'on' })}
              >
                <option value="on">ON</option>
                <option value="off">OFF</option>
              </select>
            </label>
            <label className="pref-row">
              <span>Throwables</span>
              <select
                className="field"
                value={settings.throwablesEnabled ? 'on' : 'off'}
                onChange={(e) =>
                  void patchSettings({ throwablesEnabled: e.target.value === 'on' })
                }
              >
                <option value="on">ON</option>
                <option value="off">OFF</option>
              </select>
            </label>
            <label className="pref-row">
              <span>Chat</span>
              <select
                className="field"
                value={settings.chatEnabled ? 'on' : 'off'}
                onChange={(e) => void patchSettings({ chatEnabled: e.target.value === 'on' })}
              >
                <option value="on">ON</option>
                <option value="off">OFF</option>
              </select>
            </label>
            <label className="pref-row">
              <span>Rare Events</span>
              <select
                className="field"
                value={settings.rareEventsEnabled ? 'on' : 'off'}
                onChange={(e) =>
                  void patchSettings({ rareEventsEnabled: e.target.value === 'on' })
                }
              >
                <option value="on">ON</option>
                <option value="off">OFF</option>
              </select>
            </label>
            <label className="pref-row">
              <span>Rare Event Cooldown (sec)</span>
              <input
                className="field"
                type="number"
                min={0}
                max={3600}
                value={settings.rareEventCooldownSec}
                onChange={(e) =>
                  void patchSettings({ rareEventCooldownSec: Number(e.target.value) })
                }
              />
            </label>
            <label className="pref-row">
              <span>Nuclear Tomato Chance</span>
              <input
                className="field"
                type="number"
                min={0}
                max={1}
                step={0.01}
                value={settings.nuclearTomatoProbability}
                onChange={(e) =>
                  void patchSettings({ nuclearTomatoProbability: Number(e.target.value) })
                }
              />
            </label>
            <div className="admin-actions">
              <button
                className="btn btn-gold"
                type="button"
                onClick={async () => {
                  await api.testNuclearTomato();
                  setMessage('Admin test: Nuclear Tomato triggered.');
                }}
              >
                Test Nuclear Tomato
              </button>
              <button
                className="btn"
                type="button"
                onClick={async () => {
                  await api.clearTheaterEffects();
                  setMessage('Active effects cleared.');
                }}
              >
                Clear active effects
              </button>
            </div>
          </div>
        )}

        {section === 'system' && (
          <p className="muted">
            Playback is synchronized from the server. Videos play from YouTube in the official
            embedded player. Chat history and user preferences are saved. Other theater state is
            temporary.
          </p>
        )}
      </div>
    </div>
  );
}
